import { useAtomValue } from "@effect/atom-react";
import { useParams } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";

import { classifyEnvironmentId } from "~/environments/environmentIdClassification";
import {
  usePrimaryEnvironmentCloudMode,
  usePrimaryEnvironmentId,
  usePrimaryEnvironmentIsWorkspaceProxy,
} from "~/environments/primary";
import {
  useCloudRuntimeRouteBlockState,
  type CloudRuntimeRouteBlockState,
} from "~/routes/ensureActiveRouteCloudRuntime";
import { getWsConnectionUiState, useWsConnectionStatus } from "~/rpc/wsConnectionState";
import { workspaceReadinessByEnvironmentAtom } from "~/state/workspaceReadinessAtom";
import {
  selectBootstrapCompleteForActiveEnvironment,
  selectEnvironmentState,
  useStore,
} from "~/store";

import {
  resolveWorkspaceStartupOverlayModel,
  WORKSPACE_BOOTSTRAP_SLOW_MS,
  WORKSPACE_BOOTSTRAP_TIMEOUT_MS,
  type WorkspaceStartupOverlayModel,
} from "./WorkspaceStartupOverlay.logic";

/** Matches design-principles larger transition timing (200–250ms). */
export const WORKSPACE_STARTUP_EXIT_MS = 220;

/** Crossfade between overlay status phases. */
export const WORKSPACE_STARTUP_PHASE_MS = 150;

export interface WorkspaceStartupShellState {
  readonly overlayModel: WorkspaceStartupOverlayModel | null;
  readonly isShellMounted: boolean;
  readonly isExiting: boolean;
  readonly isAppChromeVisible: boolean;
  readonly overlayVisible: boolean;
  readonly statusVisible: boolean;
  /**
   * Non-null when the routed cloud runtime cannot connect. Carries the
   * canonical status and any metadata needed for a valid recovery action.
   */
  readonly blockedState: CloudRuntimeRouteBlockState | null;
}

export function shouldShowWorkspaceAppChrome(input: {
  readonly bootstrapComplete: boolean;
  readonly isExiting: boolean;
  readonly overlayModel: WorkspaceStartupOverlayModel | null;
}): boolean {
  return input.bootstrapComplete || input.isExiting;
}

export function shouldShowWorkspaceStartupOverlay(input: {
  readonly isShellMounted: boolean;
  readonly isExiting: boolean;
  readonly isAppChromeVisible: boolean;
}): boolean {
  return input.isShellMounted && !input.isExiting && !input.isAppChromeVisible;
}

function areWorkspaceStartupOverlayModelsEqual(
  current: WorkspaceStartupOverlayModel | null,
  next: WorkspaceStartupOverlayModel | null,
): boolean {
  return current?.phase === next?.phase && current?.title === next?.title;
}

export function useWorkspaceStartupShell(): WorkspaceStartupShellState {
  const environmentId = usePrimaryEnvironmentId();
  // The active-route runtime, not the primary shell: a sleeping CMMD runtime is
  // a secondary environment selected by the route, so read the matched
  // `$environmentId` param rather than trusting activeEnvironmentId, which may
  // be unset for a runtime that has never connected.
  // Computed BEFORE effectiveBootstrapComplete because it gates the overlay's
  // very existence (below), not just its copy.
  const routeEnvironmentId = useParams({
    strict: false,
    select: (params) =>
      typeof params["environmentId"] === "string" ? params["environmentId"] : null,
  });
  const routeStoreEnvironmentId =
    routeEnvironmentId === null ? null : (routeEnvironmentId as NonNullable<typeof environmentId>);
  const activeBootstrapComplete = useStore((store) =>
    selectBootstrapCompleteForActiveEnvironment(store),
  );
  const routeBootstrapComplete = useStore((store) =>
    routeStoreEnvironmentId === null
      ? true
      : selectEnvironmentState(store, routeStoreEnvironmentId).bootstrapComplete,
  );
  const bootstrapComplete =
    routeEnvironmentId === null ? activeBootstrapComplete : routeBootstrapComplete;
  const activeEnvironmentId = useStore((store) => store.activeEnvironmentId);
  const primaryCloudMode = usePrimaryEnvironmentCloudMode();
  const cloudMode = primaryCloudMode || classifyEnvironmentId(routeEnvironmentId).isCloud;
  const isWorkspaceProxy = usePrimaryEnvironmentIsWorkspaceProxy();
  const blockedState = useCloudRuntimeRouteBlockState(routeEnvironmentId);
  const blockedKind = blockedState?.kind ?? null;
  // A workspace proxy does not emit a workspace bootstrap snapshot. Treat the
  // overlay as dismissed when no cloud runtime route is selected. A
  // non-connectable routed runtime must keep the
  // overlay mounted so its blocked-state surface is visible. Otherwise the
  // workspace-proxy shortcut suppresses stopped/error/draining/destroyed truth
  // and leaves the route on an unbounded generic startup screen.
  const canBypassUnselectedWorkspaceProxy =
    isWorkspaceProxy && activeEnvironmentId === null && routeEnvironmentId === null;
  const effectiveBootstrapComplete =
    !blockedState && (bootstrapComplete || canBypassUnselectedWorkspaceProxy);
  const wsStatus = useWsConnectionStatus();
  // FINDING-CB1: server-emitted readiness phase. Takes precedence over
  // the time-derived overlay copy when present; null when SSE hasn't
  // delivered anything yet (old server, blocked by proxy, dev-local).
  const readinessByEnvironment = useAtomValue(workspaceReadinessByEnvironmentAtom);
  const startupEnvironmentId = routeStoreEnvironmentId ?? environmentId;
  const readinessPhase = startupEnvironmentId
    ? (readinessByEnvironment[startupEnvironmentId] ?? null)
    : null;
  const [bootstrapPendingMs, setBootstrapPendingMs] = useState(0);
  const [isShellMounted, setIsShellMounted] = useState(() => !effectiveBootstrapComplete);
  const [isExiting, setIsExiting] = useState(false);
  const [statusVisible, setStatusVisible] = useState(true);
  const [committedModel, setCommittedModel] = useState<WorkspaceStartupOverlayModel | null>(() =>
    resolveWorkspaceStartupOverlayModel({
      bootstrapComplete: false,
      bootstrapPendingMs: 0,
      cloudMode,
      reconnectPhase: wsStatus.reconnectPhase,
      wsUiState: getWsConnectionUiState(wsStatus),
      readinessPhase,
      blockedKind,
    }),
  );

  useEffect(() => {
    if (effectiveBootstrapComplete) {
      setBootstrapPendingMs(0);
      return;
    }
    const startedAt = Date.now();
    const interval = window.setInterval(() => {
      setBootstrapPendingMs(Date.now() - startedAt);
    }, 1_000);
    return () => {
      window.clearInterval(interval);
    };
  }, [effectiveBootstrapComplete]);

  const wsUiState = getWsConnectionUiState(wsStatus);
  const overlayModel = useMemo(
    () =>
      resolveWorkspaceStartupOverlayModel({
        bootstrapComplete: false,
        bootstrapPendingMs,
        bootstrapSlowMs: WORKSPACE_BOOTSTRAP_SLOW_MS,
        bootstrapTimeoutMs: WORKSPACE_BOOTSTRAP_TIMEOUT_MS,
        cloudMode,
        reconnectPhase: wsStatus.reconnectPhase,
        wsUiState,
        readinessPhase,
        blockedKind,
      }),
    [
      blockedKind,
      bootstrapPendingMs,
      cloudMode,
      readinessPhase,
      wsStatus.reconnectPhase,
      wsUiState,
    ],
  );

  useEffect(() => {
    if (!effectiveBootstrapComplete) {
      setIsShellMounted(true);
      setIsExiting(false);
      return;
    }
    if (!isShellMounted) {
      return;
    }
    setIsExiting(true);
    const timeout = window.setTimeout(() => {
      setIsShellMounted(false);
      setIsExiting(false);
    }, WORKSPACE_STARTUP_EXIT_MS);
    return () => {
      window.clearTimeout(timeout);
    };
  }, [effectiveBootstrapComplete, isShellMounted]);

  useEffect(() => {
    if (!overlayModel) {
      return;
    }
    if (!committedModel || overlayModel.phase === committedModel.phase) {
      if (!areWorkspaceStartupOverlayModelsEqual(committedModel, overlayModel)) {
        setCommittedModel(overlayModel);
      }
      setStatusVisible(true);
      return;
    }
    setStatusVisible(false);
    const timeout = window.setTimeout(() => {
      setCommittedModel(overlayModel);
      setStatusVisible(true);
    }, WORKSPACE_STARTUP_PHASE_MS);
    return () => {
      window.clearTimeout(timeout);
    };
  }, [committedModel, overlayModel]);

  const displayModel = isShellMounted ? (committedModel ?? overlayModel) : null;

  const isAppChromeVisible = shouldShowWorkspaceAppChrome({
    bootstrapComplete: effectiveBootstrapComplete,
    isExiting,
    overlayModel,
  });

  return {
    overlayModel: displayModel,
    isShellMounted,
    isExiting,
    isAppChromeVisible,
    overlayVisible: shouldShowWorkspaceStartupOverlay({
      isShellMounted,
      isExiting,
      isAppChromeVisible,
    }),
    statusVisible,
    blockedState,
  };
}
